import { Action, State, DispatchThunk } from "./Store";
import { Auth, Cache } from "aws-amplify";
import {
  CognitoUser,
  OptionalCognitoUserAtrributes,
  CognitoUserAttributes,
} from "../models/CognitoUser";
import { Dispatch } from "react";
import { AxiosResponse } from "axios";
import { ILivestockUser } from "../models/ILivestockUser";
import { Business } from "../models/Business";
import * as Database from "../config/Firebase";
import { FirebaseObject } from "../models/FirebasePostObject";
import { slugify } from "../util/slug-helper";
import * as Axios from "../config/Axios";
import { DeferredLoadingFlags } from "../util";
import { Gift } from "../models/Gift";
import { Holding } from "../models/Holding";
import { BusinessFormDataType } from "../components/BusinessFieldSet/BusinessFieldSet";
import { v4 as uuid } from "uuid";
import { HoldingFormDataType } from "../components/WizardSteps/AccountSetup/HoldingStep";
import logger from "../config/Logging";
import { shutdown as shutdownIntercom } from "../components/IntercomLiveChat"
import { RoleService } from "../util/role-service/role-service";

export type QueryParam = {
  name: string;
  value?: string;
};

export enum ActionType {
  SET_USER = "setUser",
  SET_AUTHENTICATING = "setAuthenticating",
  SET_ATTRIBUTES = "setAttributes",
  SET_ACCOUNT_SETUP = "setAccountSetup",
  INITIAL_PAGE_LOAD = "initialPageLoad",
  DESTROY_SESSION = "destroySession",
  PASSWORD_CHANGED = "changePasssword",
  SET_BUSINESSES = "setBusinesses",
  SET_BUSINESS = "updateBusiness",
  SET_MISSING_BUSINESSES = "setMissingBusinesses",
  SET_GIFTS = "setGifts",
  SET_HOLDINGS = "setHoldings",
  SET_FETCHING_USER_DATA = "setFetchingUserData",
  SET_ILIVESTOCK_USER = "setIlivestockUser",
  MERGE_BUSINESSES = "mergeBusinesses",
  RESET = "reset",
  REMOVE_DEFERRED_LOADING_FLAG = "removeDeferredLoadingFlag",
  ADD_DEFERRED_LOADING_FLAG = "addDeferredLoadingFlag",
}

export const setUser = (user?: CognitoUser): Action => {
  return {
    type: ActionType.SET_USER,
    payload: user,
  };
};

export const setAuthenticating = (value: boolean): Action => {
  return {
    type: ActionType.SET_AUTHENTICATING,
    payload: value,
  };
};

export const initialPageLoad = (): Action => {
  return {
    type: ActionType.INITIAL_PAGE_LOAD,
  };
};

export const setFetchingUserData = (value: boolean): Action => {
  return {
    type: ActionType.SET_FETCHING_USER_DATA,
    payload: value,
  };
};

export const setAttributes = (attributes: CognitoUserAttributes): Action => {
  return {
    type: ActionType.SET_ATTRIBUTES,
    payload: attributes,
  };
};

export const setBusinesses = (
  businesses: FirebaseObject<Business>[]
): Action => {
  return {
    type: ActionType.SET_BUSINESSES,
    payload: businesses,
  };
};

export const setBusiness = (business: FirebaseObject<Business>): Action => {
  return {
    type: ActionType.SET_BUSINESS,
    payload: business,
  };
};

export const setMissingBusinesses = (businesses: Business[]): Action => {
  return {
    type: ActionType.SET_MISSING_BUSINESSES,
    payload: businesses,
  };
};

export const setGifts = (gifts: Gift[]): Action => {
  return {
    type: ActionType.SET_GIFTS,
    payload: gifts,
  };
};

export const setHoldings = (holdings: FirebaseObject<Holding>[]): Action => {
  return {
    type: ActionType.SET_HOLDINGS,
    payload: holdings,
  };
};

export const mergeBusinesses = (businesses: any) => {
  return {
    type: ActionType.MERGE_BUSINESSES,
    payload: businesses,
  };
};

export const setIlivestockUser = (ilivestockUser: ILivestockUser): Action => {
  return {
    type: ActionType.SET_ILIVESTOCK_USER,
    payload: ilivestockUser,
  };
};

export const reset = () => {
  return {
    type: ActionType.RESET,
  };
};

export const removeDeferredLoadingFlags = (
  ...flags: DeferredLoadingFlags[]
) => {
  return {
    type: ActionType.REMOVE_DEFERRED_LOADING_FLAG,
    payload: flags,
  };
};

export const addDeferredLoadingFlags = (...flags: DeferredLoadingFlags[]) => {
  return {
    type: ActionType.ADD_DEFERRED_LOADING_FLAG,
    payload: flags,
  };
};

export const fetchHoldings = (
  code: string
) => {
  return async function fetchHoldings(
    dispatch: Dispatch<Action>,
    state: State
  ) {
    try {
      const { data: holdings }: AxiosResponse<FirebaseObject<Holding>[]> = await Axios.get({
        path: `/businesses/${code}/holdings`,
      })
      dispatch(setHoldings(holdings));
    } catch (error) {
      logger.log("error", "Fetch holdings", {
        additionalInfo: {
          tags: "holdings",
          body: error,
          file: "Store.Actions",
          user: state.ilivestockUser?.sub,
        },
      });
      dispatch(setHoldings([]));
    };
  };
};

export const destroySession = () => {
  return async function destroySession(
    dispatch: Dispatch<Action>,
    state: State
  ) {
    try {
      shutdownIntercom()
      await Auth.signOut();
      dispatch(reset());
    } catch (err) {
      logger.log("error", "Destroy session", {
        additionalInfo: {
          tags: "session",
          body: err,
          file: "Store.Actions",
          user: state.ilivestockUser?.sub,
        },
      });
      throw err;
    }
  };
};

export const createSession = (email: string, password: string) => {
  return async function createSession(
    dispatch: Dispatch<Action>,
    state: State
  ) {
    const sanitizedEmail = email.toLowerCase().trim();
    const cognitoUser: CognitoUser = await Auth.signIn(
      sanitizedEmail,
      password
    ).catch((error) => {
      logger.log("error", "Create session", {
        additionalInfo: {
          tags: "session",
          body: error,
          file: "Store.Actions",
          user: null,
          email: email,
        },
      });
      throw error;
    });

    // This shouldn't really trigger but we catch it anyway
    if (!cognitoUser) throw Error("Error creating cognito user session");

    // Only Trade partners can access the portal
    const isPartner = new RoleService().hasRoles(['partners'], cognitoUser).oneOf();
    if (!isPartner) {
      await Auth.signOut();
      const err = {
        code: "NotTradePartner",
        name: "NotTradePartner",
      }
      throw err;
    }

    // Update state with the entire user
    dispatch(setUser(cognitoUser));
    return cognitoUser;
  };
};

export const forceSessionRefresh = () => {
  return async function forceSessionRefresh(
    dispatch: Dispatch<Action>,
    state: State
  ) {
    try {
      const updatedUser: CognitoUser = await Auth.currentAuthenticatedUser({
        bypassCache: true,
      });
      dispatch(setUser(updatedUser));
    } catch (error) {
      logger.log("error", "Force refresh session", {
        additionalInfo: {
          tags: "session",
          body: error,
          file: "Store.Actions",
          user: state.ilivestockUser?.sub,
        },
      });
      throw error;
    }
  };
};

export const changePassword = (oldPassword: string, newPassword: string) => {
  return async function changePassword(
    dispatch: Dispatch<Action>,
    state: State
  ) {
    try {
      const user: CognitoUser = await Auth.currentAuthenticatedUser();
      await Auth.changePassword(user, oldPassword, newPassword);
      dispatch({
        type: ActionType.PASSWORD_CHANGED,
      });

      logger.log("info", "Change password", {
        additionalInfo: {
          tags: "password",
          body: null,
          file: "Store.Actions",
          user: state.ilivestockUser?.sub,
        },
      });

      return;
    } catch (err) {
      logger.log("error", "Change password", {
        additionalInfo: {
          tags: "password",
          body: err,
          file: "Store.Actions",
          user: state.ilivestockUser?.sub,
        },
      });

      throw err;
    }
  };
};

export const confirmAccount = (email: string, code: string) => {
  return async function confirmAccount(dispatch: Dispatch<Action>) {
    try {
      await Auth.confirmSignUp(email, code);

      logger.log("info", "Confirm account", {
        additionalInfo: {
          tags: "account",
          body: null,
          file: "Store.Actions",
          user: email,
        },
      });

      return;
    } catch (err) {
      dispatch(setUser(undefined));
      logger.log("error", "Confirm account", {
        additionalInfo: {
          tags: "account",
          body: err,
          file: "Store.Actions",
          user: null,
          email: email,
        },
      });

      throw err;
    }
  };
};

export const resetPasswordRequest = (email: string) => {
  return async function resetPasswordRequest(dispatch: Dispatch<Action>) {
    try {
      await Auth.forgotPassword(email);

      logger.log("info", "Reset password request", {
        additionalInfo: {
          tags: "password",
          body: null,
          file: "Store.Actions",
          user: null,
          email: email,
        },
      });

      return;
    } catch (err) {
      logger.log("error", "Reset password request", {
        additionalInfo: {
          tags: "password",
          body: err,
          file: "Store.Actions",
          user: null,
          email: email,
        },
      });

      throw err;
    }
  };
};

export const resetPassword = (
  email: string,
  code: string,
  password: string
) => {
  return async function resetPassword(dispatch: Dispatch<Action>) {
    try {
      await Auth.forgotPasswordSubmit(email, code, password);
      Cache.removeItem("resetEmailRequest");

      logger.log("info", "Reset password", {
        additionalInfo: {
          tags: "password",
          body: null,
          file: "Store.Actions",
          user: null,
          email: email,
        },
      });

      return;
    } catch (err) {
      logger.log("error", "Reset password", {
        additionalInfo: {
          tags: "password",
          body: err,
          file: "Store.Actions",
          user: null,
          email: email,
        },
      });

      throw err;
    }
  };
};

export const resendCode = (email: string) => {
  return async function resendCode(dispatch: Dispatch<Action>) {
    try {
      await Auth.resendSignUp(email);

      logger.log("info", "Resend code", {
        additionalInfo: {
          tags: "code",
          body: null,
          file: "Store.Actions",
          user: null,
          email: email,
        },
      });
    } catch (err) {
      logger.log("error", "Resend code", {
        additionalInfo: {
          tags: "code",
          body: err,
          file: "Store.Actions",
          user: null,
          email: email,
        },
      });

      throw err;
    }
  };
};

export const autoSignIn = (email: string, token: string) => {
  return async function autoSignIn(dispatch: Dispatch<Action>) {
    try {
      const otp = token;
      const result = await Auth.signIn(email);
      const user: CognitoUser = await Auth.sendCustomChallengeAnswer(
        result,
        otp
      );
      logger.log("info", "Token auth", {
        additionalInfo: {
          tags: "session",
          file: "Store.Actions",
          user: user.username,
        },
      });
      dispatch(setUser(user));
      dispatch(removeDeferredLoadingFlags(DeferredLoadingFlags.SILENT_AUTH));

      return;
    } catch (err) {
      dispatch(setUser(undefined));
      logger.log("error", "Token auth", {
        additionalInfo: {
          tags: "session",
          file: "Store.Actions",
          error: err,
          user: null,
        },
      });

      throw err;
    }
  };
};

export const silentAuth = () => {
  return async function silentAuth(dispatch: Dispatch<Action>) {
    try {
      const user: CognitoUser = await Auth.currentAuthenticatedUser();
      dispatch(setUser(user));
      dispatch(removeDeferredLoadingFlags(DeferredLoadingFlags.SILENT_AUTH));
      if (process.env.NODE_ENV !== "production") {
        const color = "color: #03fc98";
        console.group("Current Authenticated User");
        console.debug("%cGiven Name: ", color, user.attributes.given_name);
        console.debug("%cFamily Name: ", color, user.attributes.family_name);
        console.debug("%cSub: ", color, user.attributes.sub);
        console.debug(
          "%cID Token: ",
          color,
          user.signInUserSession?.idToken.jwtToken
        );
        console.debug(
          "%cAccess Token: ",
          color,
          user.signInUserSession?.accessToken.jwtToken
        );
        console.groupEnd();
      }

      return;
    } catch (err) {
      dispatch(setUser(undefined));

      logger.log("debug", "Silent auth", {
        additionalInfo: {
          tags: "session",
          file: "Store.Actions",
          error: err,
          user: null,
        },
      });

      throw err;
    }
  };
};

export const getUserAttributes = () => {
  return async function getUserAttributes(
    dispatch: Dispatch<Action>,
    state: State
  ) {
    try {
      const info: any = await Auth.currentUserInfo();
      dispatch(setAttributes(info.attributes));
      return;
    } catch (err) {
      logger.log("error", "Get user attributes", {
        additionalInfo: {
          tags: "user",
          user: state.ilivestockUser?.sub,
          file: "Store.Actions",
          error: err,
        },
      });

      throw err;
    }
  };
};

export const updateUserAttributes = (
  attributes: OptionalCognitoUserAtrributes
) => {
  return async function updateUserAttributes(
    dispatch: Dispatch<Action>,
    state: State
  ) {
    logger.log("info", "Update user attributes", {
      additionalInfo: {
        tags: "user",
        user: state.ilivestockUser?.sub,
        attributes,
        file: "Store.Actions",
      },
    });

    try {
      await Auth.updateUserAttributes(state.user, attributes);
      const updatedUser: CognitoUser = await Auth.currentAuthenticatedUser({
        bypassCache: true,
      });
      dispatch(setUser(updatedUser));
      return;
    } catch (err) {
      logger.log("error", "Update user attributes", {
        additionalInfo: {
          tags: "user",
          body: err,
          user: state.ilivestockUser?.sub,
          file: "Store.Actions",
        },
      });

      throw err;
    }
  };
};

export const createHolding = (
  values: HoldingFormDataType,
  code: string,
  id?: string
) => {
  return async function createHolding(dispatch: DispatchThunk, state: State) {
    const { address, cphNumber, name, flockMark, herdMark, postcode } = values;
    const _id = id || uuid().toUpperCase();
    const cphcounty = cphNumber?.slice(0, 2);
    const cphparish = cphNumber?.slice(2, 5);
    const cphkeeper = cphNumber?.slice(5, cphNumber.length);

    try {
      const firebaseObj: FirebaseObject<Holding> = {
        data: {
          attributes: {
            id: _id,
            primaryHolding: true,
            address,
            cphcounty,
            cphkeeper,
            cphparish,

            name,
            flockMark,
            herdMark,
            postcode,
          },
          id: _id,
          type: "holding",
        },
      };
      const ref = await Database.ref(`/${code}/Holding/${_id}`);
      await ref.set(firebaseObj);
      logger.log("info", "Create holding", {
        additionalInfo: {
          tags: "holding",
          body: values,
          file: "Store.Actions",
          user: state.ilivestockUser?.sub,
        },
      });
    } catch (error) {
      logger.log("error", "Create holding", {
        additionalInfo: {
          tags: "holding",
          body: error,
          user: state.ilivestockUser?.sub,
          file: "Store.Actions",
        },
      });

      throw error;
    }
  };
};

export const updateHolding = (
  values: HoldingFormDataType,
  code: string,
  id?: string
) => {
  return async function updateHolding(dispatch: DispatchThunk, state: State) {
    const { address, country, cphNumber, name, flockMark, herdMark, postcode } = values;
    const _id = id || uuid().toUpperCase();
    const cphcounty = cphNumber?.slice(0, 2);
    const cphparish = cphNumber?.slice(2, 5);
    const cphkeeper = cphNumber?.slice(5, cphNumber.length);

    try {
      const firebaseObj: FirebaseObject<Holding> = {
        data: {
          attributes: {
            id: _id,
            primaryHolding: true,
            address,
            cphcounty,
            cphkeeper,
            cphparish,
            country,
            name,
            flockMark,
            herdMark,
            postcode,
          },
          id: _id,
          type: "holding",
        },
      };
      const ref = await Database.ref(`/${code}/Holding/${_id}`);
      await ref.set(firebaseObj);

      logger.log("info", "Update holding", {
        additionalInfo: {
          tags: "holding",
          body: values,
          file: "Store.Actions",
          user: state.ilivestockUser?.sub,
        },
      });
    } catch (error) {
      logger.log("error", "Update holding", {
        additionalInfo: {
          tags: "holding",
          body: error,
          user: state.ilivestockUser?.sub,
          file: "Store.Actions",
        },
      });

      throw error;
    }
  };
};

export const createBusiness = () => {
  return async function createBusiness(dispatch: DispatchThunk, state: State) {
    try {
      const res = await Axios.post({
        path: "/users/businesses",
      });

      logger.log("info", "Create business", {
        additionalInfo: {
          tags: "business",
          body: null,
          file: "Store.Actions",
          user: state.ilivestockUser?.sub,
        },
      });

      return res;
    } catch (error) {
      logger.log("error", "Create business", {
        additionalInfo: {
          tags: "business",
          body: error,
          user: state.ilivestockUser?.sub,
          file: "Store.Actions",
        },
      });

      throw error;
    }
  };
};

export const updateBusiness = (
  values: BusinessFormDataType,
  business: FirebaseObject<Business>
) => {
  return async function updateBusiness(dispatch: DispatchThunk, state: State) {
    const {
      contactFirstName,
      contactSurname,
      nameLegal,
      nameShort,
      phoneMobile,
    } = values;
    const { id } = business.data;
    const { code } = business.data.attributes;
    const firebaseObj: FirebaseObject<BusinessFormDataType> = {
      data: {
        id,
        type: "Business",
        attributes: {
          contactSurname,
          contactFirstName,
          nameLegal,
          nameShort,
          phoneMobile,
        },
      },
    };
    try {
      const ref = await Database.ref(`/${code}`);
      await ref.set({
        Business: {
          [id]: firebaseObj,
        },
      });

      logger.log("info", "Update business", {
        additionalInfo: {
          tags: "business",
          body: values,
          file: "Store.Actions",
          user: state.ilivestockUser?.sub,
        },
      });
    } catch (error) {
      logger.log("error", "Update business", {
        additionalInfo: {
          tags: "business",
          body: error,
          user: state.ilivestockUser?.sub,
          file: "Store.Actions",
        },
      });

      throw error;
    }
  };
};

export const fetchBusinesses = () => {
  return async function fetchBusinesses(
    dispatch: Dispatch<Action>,
    state: State
  ) {
    try {
      const result = await Axios.get<Business[]>({ path: "/users/businesses" });
      if (!result.data.length) {
        dispatch(setBusinesses([]));
        return;
      }
      const cache: FirebaseObject<Business>[] = [];
      const missingBusinessesCache: Business[] = [];
      for await (const business of result.data) {
        const ref = await Database.ref(`/${business.code}`);

        ref.once("value", async (obj) => {
          const tree: {
            Business?: {
              [key: string]: FirebaseObject<Business>;
            };
          } | null = obj.val();

          if (!tree?.Business) {
            missingBusinessesCache.push(business);
            dispatch(setMissingBusinesses(missingBusinessesCache));

            return;
          }

          const firebaseBusiness: FirebaseObject<Business> = Object.entries(
            tree.Business
          )[0][1];
          firebaseBusiness.data.attributes.slug = slugify(business.code);
          firebaseBusiness.data.attributes.subscription = business.subscription;
          firebaseBusiness.data.attributes.code = business.code;
          firebaseBusiness.data.attributes.isOwner = business.isOwner;

          cache.push(firebaseBusiness);
          dispatch(setBusinesses(cache));
        });
      }
    } catch (error) {
      logger.log("error", "Fetch business", {
        additionalInfo: {
          tags: "business",
          body: error,
          file: "Store.Actions",
          user: state.ilivestockUser?.sub,
        },
      });

      dispatch(setBusinesses([]));
    }
  };
};

export const fetchGifts = () => {
  return async function fetchGifts(dispatch: Dispatch<Action>, state: State) {
    try {
      const result = await Axios.get<Gift[]>({ path: "/users/gifts" });
      dispatch(setGifts(result.data));
    } catch (error) {
      logger.log("error", "Fetch gifts", {
        additionalInfo: {
          tags: "gifts",
          body: error,
          file: "Store.Actions",
          user: state.ilivestockUser?.sub,
        },
      });

      dispatch(setGifts([]));
    }
    dispatch(removeDeferredLoadingFlags(DeferredLoadingFlags.GIFTS));
  };
};

export const fetchILivestockUser = (queries?: QueryParam[]) => {
  return async function fetchILivestockUser(dispatch: Dispatch<Action>) {
    /**
     * Whenever we are creating a session, we need to ensure the cognito user
     * is synced with our API.
     * TODO all this should be moved to a lambda post confirm hook!
     */
    let url = "/users";
    if (queries)
      url =
        `${url}?` +
        queries
          .map((query) => {
            const { name, value } = query;
            if (!value) return name;
            return `${name}=${value}`;
          })
          .join("&");

    try {
      // Try and get the user
      const response = await Axios.get<ILivestockUser>({
        path: url,
      });
      dispatch(setIlivestockUser(response.data));
    } catch (error) {
      logger.log("error", "Fetch iLivestock user", {
        additionalInfo: {
          tags: "user",
          body: error,
          file: "Store.Actions",
          user: null,
        },
      });

      throw error;
    }
  };
};

export const firebaseSyncBusinesses = () => {
  return async function firebaseSyncBusinesses(
    dispatch: Dispatch<Action>,
    state: State
  ) {
    const businesses = state.businesses;
    if (businesses) {
      const toBeMerged: Business[] = [];
      for (let index = 0; index < businesses.length; index++) {
        const business = businesses[index];
        const ref = await Database.ref(
          `/${business?.data.attributes.code}/Business`
        );
        const result = await ref.once("value");
        const values: {
          [key: string]: FirebaseObject<Business>;
        } = result.val();
        if (values) {
          const firebaseBusiness = Object.entries(values);
          firebaseBusiness.forEach(([id, obj]) => {
            const slug = slugify(obj.data.attributes.code);
            toBeMerged.push({
              ...business,
              ...obj.data.attributes,
              id,
              slug,
            });
          });
        }
      }
      dispatch(mergeBusinesses(toBeMerged));
    }
    dispatch(
      removeDeferredLoadingFlags(DeferredLoadingFlags.FIREBASE_SYNC_BUSINESSES)
    );
  };
};
